<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { Modal, LoadingSpinner } from "@/components";
import { auth } from "@/fire";
import { UploadService } from "@/util/UploadService";
import { getStorageProxyUrl } from "@/util";
import { MimeType } from "@/util/MimeType";
import {
  ProductUsageFeatureKey,
  trackFeatureUsage,
} from "@/util/ProductUsageAnalytics";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import type { WorkspaceDocumentFragment } from "@/graphql/operations";

const props = defineProps<{
  active: boolean;
  doc: WorkspaceDocumentFragment;
}>();

const emit = defineEmits<{
  (e: "success"): void;
  (e: "close"): void;
}>();

const store = useStore();
const { t } = useI18n();

type Source = "upload" | "videoframe";
type ThumbnailSource = "upload" | "video_frame";

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ACCEPTED_IMAGE_TYPES_ATTRIBUTE = ACCEPTED_IMAGE_TYPES.join(",");
const BACKEND_UNSUPPORTED_IMAGE_TYPE_DETAIL = "Unsupported image type";

const isVideo = MimeType.isVideo(props.doc.mimeType);
const isAudio = MimeType.isAudio(props.doc.originalMimeType);
// "Choose from video" only for real videos, not for audio wrapped as mp4.
const showVideoFrameSource = isVideo && !isAudio;
const assetType = isAudio ? "audio" : isVideo ? "video" : "document";

const activeSource = ref<Source>(
  showVideoFrameSource ? "videoframe" : "upload",
);
const isUploading = ref(false);
const errorMessage = ref<string | null>(null);
const dragOver = ref(false);
const selectedImageBlob = ref<Blob | null>(null);
// When true, the captured frame is shown frozen instead of the live video.
const videoFrozen = ref(false);

function clearSelection() {
  errorMessage.value = null;
  selectedImageBlob.value = null;
  videoFrozen.value = false;
}

function switchSource(source: Source) {
  if (activeSource.value === source) return;
  activeSource.value = source;
  clearSelection();
}

const close = () => {
  if (isUploading.value) return;
  cleanup();
  emit("close");
};

const cleanup = () => {
  errorMessage.value = null;
  selectedImageBlob.value = null;
  videoFrozen.value = false;
};

const canSubmit = computed(
  () => selectedImageBlob.value !== null && !isUploading.value,
);

const objectUrl = ref("");

watch(selectedImageBlob, (newBlob) => {
  if (objectUrl.value) {
    window.URL.revokeObjectURL(objectUrl.value);
  }
  objectUrl.value = newBlob ? window.URL.createObjectURL(newBlob) : "";
});

async function getAuthToken(): Promise<string> {
  return await auth.currentUser!.getIdToken();
}

function thumbnailSource(): ThumbnailSource {
  return activeSource.value === "videoframe" ? "video_frame" : "upload";
}

function responseDetail(error: unknown): string {
  const message = (error as { message?: string }).message;
  if (!message) return "";
  try {
    const body = JSON.parse(message);
    return typeof body.detail === "string" ? body.detail : message;
  } catch {
    return message;
  }
}

function isUnsupportedImageTypeError(error: unknown) {
  return responseDetail(error).includes(BACKEND_UNSUPPORTED_IMAGE_TYPE_DETAIL);
}

async function submit() {
  if (!selectedImageBlob.value) return;
  isUploading.value = true;
  errorMessage.value = null;

  const formData = new FormData();
  formData.append("file", selectedImageBlob.value, "thumbnail.jpg");

  try {
    await UploadService.replaceThumbnail(
      formData,
      await getAuthToken(),
      store.getters.currentWorkspace.id,
      props.doc.id,
    );
    trackFeatureUsage(ProductUsageFeatureKey.ThumbnailReplaced, {
      source: "legacy",
      assetType,
      thumbnailSource: thumbnailSource(),
    });
    emit("success");
    emit("close");
  } catch (e) {
    const status = (e as { status?: number }).status;
    if (status === 409) {
      errorMessage.value = t("pvDocEditor.thumbnailReplace.busy");
    } else if (status === 413) {
      errorMessage.value = t("pvDocEditor.thumbnailReplace.tooLarge");
    } else if (status === 400 && isUnsupportedImageTypeError(e)) {
      errorMessage.value = t("pvDocEditor.thumbnailReplace.unsupportedType");
    } else {
      errorMessage.value = t("pvDocEditor.thumbnailReplace.error");
    }
  } finally {
    isUploading.value = false;
  }
}

// --- Source: image upload ---
function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    setImageFromFile(input.files[0]);
  }
}

function setImageFromFile(file: File) {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    selectedImageBlob.value = null;
    errorMessage.value = t("pvDocEditor.thumbnailReplace.unsupportedType");
    return;
  }
  selectedImageBlob.value = file;
  errorMessage.value = null;
}

function onDrop(event: DragEvent) {
  dragOver.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file) {
    setImageFromFile(file);
  }
}

function onDragOver() {
  dragOver.value = true;
}

function onDragLeave() {
  dragOver.value = false;
}

// --- Source: clipboard paste ---
// Accept images pasted via Ctrl+V (e.g. from the GNOME screenshot tool).
function handlePaste(event: ClipboardEvent) {
  if (!props.active) return;
  const items = event.clipboardData?.items;
  if (!items) return;
  for (const item of items) {
    if (item.type.startsWith("image/")) {
      const file = item.getAsFile();
      if (file) {
        event.preventDefault();
        // Switch to the upload tab so the pasted image is visible.
        activeSource.value = "upload";
        videoFrozen.value = false;
        setImageFromFile(file);
        return;
      }
    }
  }
}

onMounted(() => {
  window.addEventListener("paste", handlePaste);
});

onBeforeUnmount(() => {
  window.removeEventListener("paste", handlePaste);
  cleanup();
  if (objectUrl.value) {
    window.URL.revokeObjectURL(objectUrl.value);
  }
});

// --- Source: video frame ---
const videoFrameVideo = ref<HTMLVideoElement | null>(null);

function captureVideoFrame() {
  const video = videoFrameVideo.value;
  if (!video || !video.videoWidth) {
    errorMessage.value = t("pvDocEditor.thumbnailReplace.videoNotReady");
    return;
  }
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    errorMessage.value = t("pvDocEditor.thumbnailReplace.error");
    return;
  }
  try {
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          selectedImageBlob.value = blob;
          videoFrozen.value = true;
          errorMessage.value = null;
        }
      },
      "image/jpeg",
      0.9,
    );
  } catch {
    errorMessage.value = t("pvDocEditor.thumbnailReplace.error");
  }
}

function resumeVideo() {
  videoFrozen.value = false;
  selectedImageBlob.value = null;
}
</script>

<template>
  <Modal
    :active="active"
    @close="close"
    class="medium"
    :scrolling="true"
    :shrinkToContent="true"
    data-test="thumbnail-replace-modal"
  >
    <template v-slot:header>
      {{ $t("pvDocEditor.thumbnailReplace.title") }}
    </template>
    <template v-slot:body>
      <div class="thumbnail_replace">
        <div v-if="isUploading" class="loading_overlay">
          <LoadingSpinner :color="'#34b5ba'" />
        </div>
        <div class="source_tabs">
          <button
            :class="{ active: activeSource === 'upload' }"
            @click="switchSource('upload')"
          >
            {{ $t("pvDocEditor.thumbnailReplace.upload") }}
          </button>
          <button
            v-if="showVideoFrameSource"
            :class="{ active: activeSource === 'videoframe' }"
            @click="switchSource('videoframe')"
          >
            {{ $t("pvDocEditor.thumbnailReplace.videoFrame") }}
          </button>
        </div>

        <!-- Upload source -->
        <div v-if="activeSource === 'upload'" class="source_body">
          <label
            class="dropzone"
            :class="{ drag: dragOver, has_image: !!selectedImageBlob }"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
          >
            <input
              type="file"
              :accept="ACCEPTED_IMAGE_TYPES_ATTRIBUTE"
              @change="onFileSelected"
            />
            <span v-if="!selectedImageBlob" class="dropzone_hint">
              {{ $t("pvDocEditor.thumbnailReplace.dropHint") }}
            </span>
            <img v-else :src="objectUrl" class="preview" />
          </label>
        </div>

        <!-- Video frame source -->
        <div v-if="activeSource === 'videoframe'" class="source_body">
          <!-- Keep the <video> mounted (v-show) so its playback position is
               preserved when toggling to the frozen frame and back. -->
          <video
            v-show="!videoFrozen"
            ref="videoFrameVideo"
            class="preview_video"
            crossorigin="anonymous"
            controls
            :src="getStorageProxyUrl(doc.downloadUrl)"
          ></video>
          <img v-show="videoFrozen" :src="objectUrl" class="preview_video" />
          <div class="actions" v-show="!videoFrozen">
            <button class="button" @click="captureVideoFrame">
              {{ $t("pvDocEditor.thumbnailReplace.captureFrame") }}
            </button>
          </div>
          <div class="actions" v-show="videoFrozen">
            <button class="button secondary" @click="resumeVideo">
              {{ $t("pvDocEditor.thumbnailReplace.resumeVideo") }}
            </button>
          </div>
        </div>

        <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
      </div>
    </template>
    <template v-slot:footer>
      <div class="footer_actions">
        <button class="button secondary" @click="close">
          {{ $t("pvDocEditor.thumbnailReplace.cancel") }}
        </button>
        <button class="button primary" :disabled="!canSubmit" @click="submit">
          <span>{{ $t("pvDocEditor.thumbnailReplace.submit") }}</span>
        </button>
      </div>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
.thumbnail_replace {
  position: relative;
  min-height: 200px;
}

.loading_overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--classic-surface, #fff) 70%, transparent);
  z-index: 10;
}

.source_tabs {
  display: flex;
  gap: 0.5em;
  margin-bottom: 1em;
  border-bottom: 1px solid var(--classic-border-strong, #cecece);
}

.source_tabs button {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 0.5em 1em;
  cursor: pointer;
  font-size: 0.9em;
  color: var(--classic-text-secondary, #555);
}

.source_tabs button.active {
  border-bottom-color: var(--classic-accent, #34b5ba);
  color: var(--classic-accent, #34b5ba);
  font-weight: 600;
}

.source_body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1em;
}

.dropzone {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 160px;
  border: 2px dashed var(--classic-border-strong, #cecece);
  border-radius: 4px;
  cursor: pointer;
  background: var(--classic-media-surface, #fafafa);
}

.dropzone.drag {
  border-color: var(--classic-accent, #34b5ba);
  background: var(--classic-selection, #e8f8f8);
}

.dropzone.has_image {
  border-style: solid;
}

.dropzone input[type="file"] {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.dropzone_hint {
  color: var(--classic-text-muted, #888);
  font-size: 0.9em;
  padding: 2em 1.5em;
  text-align: center;
}

.preview {
  max-width: 100%;
  max-height: 200px;
  object-fit: contain;
}

.preview_video {
  max-width: 100%;
  max-height: 260px;
}

.actions {
  display: flex;
  gap: 0.5em;
}

.error {
  color: var(--classic-error-text, #e53935);
  margin-top: 0.8em;
  font-size: 0.9em;
}

.footer_actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5em;
}

.button {
  padding: 0.5em 1.2em;
  border: 1px solid var(--classic-accent, #34b5ba);
  border-radius: 3px;
  background: var(--classic-surface, #fff);
  color: var(--classic-accent, #34b5ba);
  cursor: pointer;
  font-size: 0.9em;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 0.4em;
}

.button:hover:not(:disabled) {
  background: var(--classic-accent, #34b5ba);
  color: var(--classic-on-accent, #fff);
}

.button.primary {
  background: var(--classic-accent, #34b5ba);
  color: var(--classic-on-accent, #fff);
}

.button.primary:hover:not(:disabled) {
  background: var(--classic-accent-hover, #2a9da3);
}

.button.secondary {
  border-color: var(--classic-border-strong, #cecece);
  color: var(--classic-text-secondary, #555);
}

.button.secondary:hover:not(:disabled) {
  background: var(--classic-surface-hover, #f0f0f0);
  color: var(--classic-text, #333);
}

.button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
